import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    ArrowLeft,
    CheckCircle,
    Loader2,
    Monitor,
    Wifi,
    WifiOff,
} from 'lucide-react'

import type {
    CustomerDisplayCartEvent,
    CustomerDisplayCartItem,
    CustomerDisplayCheckoutEvent,
    CustomerDisplayPaidEvent,
    CustomerDisplayQREvent,
} from '@/hooks/use-customer-display-sse'

import { useCustomerDisplaySSE } from '@/hooks/use-customer-display-sse'
import { usePaymentQR } from '@/hooks/use-sales-order-query'
import { formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/sale/display')({
    component: CustomerDisplayPage,
})

type DisplayState =
    | { mode: 'idle' }
    | {
          mode: 'cart'
          items: CustomerDisplayCartItem[]
          total: number
          customerName: string
      }
    | {
          mode: 'checkout'
          orderCode: string
          total: number
          paid: number
          change: number
      }
    | { mode: 'qr'; orderId: string; orderCode: string; amount: number }
    | { mode: 'success'; orderCode: string; amount: number }

function CustomerDisplayPage() {
    // _authed beforeLoad guarantees user is authenticated before this renders
    return <CustomerDisplayView />
}

export function CustomerDisplayView() {
    const navigate = useNavigate()
    const exitKiosk = () => navigate({ to: '/', search: { kioskExit: true } })
    const [state, setState] = useState<DisplayState>({ mode: 'idle' })
    const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const handleShowQR = useCallback((event: CustomerDisplayQREvent) => {
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current)
        setState({
            mode: 'qr',
            orderId: event.order_id,
            orderCode: event.order_code,
            amount: event.total_amount,
        })
    }, [])

    const handlePaid = useCallback((event: CustomerDisplayPaidEvent) => {
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current)
        setState({
            mode: 'success',
            orderCode: event.order_code,
            amount: event.paid_amount,
        })
        clearTimerRef.current = setTimeout(
            () => setState({ mode: 'idle' }),
            5_000,
        )
    }, [])

    const handleCart = useCallback((event: CustomerDisplayCartEvent) => {
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current)
        setState({
            mode: 'cart',
            items: event.items,
            total: event.total,
            customerName: event.customer_name,
        })
    }, [])

    const handleCheckout = useCallback(
        (event: CustomerDisplayCheckoutEvent) => {
            if (clearTimerRef.current) clearTimeout(clearTimerRef.current)
            setState({
                mode: 'checkout',
                orderCode: event.order_code,
                total: event.total,
                paid: event.paid,
                change: event.change,
            })
        },
        [],
    )

    const handleClear = useCallback(() => {
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current)
        setState({ mode: 'idle' })
    }, [])

    const sseHandlers = useMemo(
        () => ({
            onShowQR: handleShowQR,
            onPaid: handlePaid,
            onCart: handleCart,
            onCheckout: handleCheckout,
            onClear: handleClear,
        }),
        [handleShowQR, handlePaid, handleCart, handleCheckout, handleClear],
    )

    const { connected } = useCustomerDisplaySSE(sseHandlers)

    // WakeLock API — prevent tablet screen from sleeping
    useEffect(() => {
        let wakeLock: WakeLockSentinel | null = null
        const request = async () => {
            try {
                wakeLock = await navigator.wakeLock.request('screen')
            } catch {
                /* not supported or denied */
            }
        }
        void request()
        const onVisible = () => {
            if (document.visibilityState === 'visible') void request()
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            void wakeLock?.release()
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [])

    // Clean up timer on unmount
    useEffect(() => {
        return () => {
            if (clearTimerRef.current) clearTimeout(clearTimerRef.current)
        }
    }, [])

    return (
        <div className="min-h-screen bg-white text-gray-900 flex flex-col">
            {/* Discreet exit — staff-only escape from the buyer-facing kiosk */}
            <button
                type="button"
                onClick={exitKiosk}
                aria-label={m.common_aria_back()}
                className="fixed top-3 left-3 z-50 inline-flex size-8 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            >
                <ArrowLeft className="size-4" />
            </button>
            {state.mode === 'idle' && <IdleView connected={connected} />}
            {state.mode === 'cart' && (
                <CartView
                    items={state.items}
                    total={state.total}
                    customerName={state.customerName}
                    connected={connected}
                />
            )}
            {state.mode === 'checkout' && (
                <CheckoutView
                    orderCode={state.orderCode}
                    total={state.total}
                    paid={state.paid}
                    change={state.change}
                />
            )}
            {state.mode === 'qr' && (
                <QRView
                    orderId={state.orderId}
                    orderCode={state.orderCode}
                    amount={state.amount}
                    connected={connected}
                />
            )}
            {state.mode === 'success' && (
                <SuccessView
                    orderCode={state.orderCode}
                    amount={state.amount}
                />
            )}
        </div>
    )
}

function IdleView({ connected }: { connected: boolean }) {
    return (
        <div className="flex-1 flex flex-col items-center justify-center gap-6">
            <Monitor className="h-20 w-20 text-primary/40" />
            <h1 className="text-3xl font-bold text-gray-700">
                {m.customer_display_welcome()}
            </h1>
            <StatusBar connected={connected} />
        </div>
    )
}

function QRView({
    orderId,
    orderCode,
    amount,
    connected,
}: {
    orderId: string
    orderCode: string
    amount: number
    connected: boolean
}) {
    const { data: qr, isLoading } = usePaymentQR(orderId, true)

    return (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 p-6">
            <h2 className="text-xl font-semibold text-primary">
                {m.customer_display_scan_to_pay()}
            </h2>

            {isLoading ? (
                <Loader2 className="h-16 w-16 animate-spin text-gray-300" />
            ) : qr ? (
                <>
                    <img
                        src={qr.qr_url}
                        alt="QR Code"
                        className="w-72 h-72 rounded-xl border-2 border-gray-200 p-2 object-contain"
                    />

                    <div className="text-center space-y-2">
                        <div className="space-y-1 text-sm text-gray-500">
                            <p>{qr.bank_name}</p>
                            <p>
                                {m.payment_qr_account_holder()}:{' '}
                                <span className="text-gray-900 font-medium">
                                    {qr.bank_holder}
                                </span>
                            </p>
                            <p>
                                {m.payment_qr_account_number()}:{' '}
                                <span className="font-mono text-gray-900">
                                    {qr.bank_account}
                                </span>
                            </p>
                            <p>
                                {m.payment_qr_content()}:{' '}
                                <span className="font-mono text-gray-900">
                                    {orderCode}
                                </span>
                            </p>
                        </div>

                        <div className="text-3xl font-bold text-primary mt-4">
                            {formatVND(amount)}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{m.customer_display_waiting()}</span>
                    </div>
                </>
            ) : (
                <p className="text-gray-400">{m.payment_qr_error()}</p>
            )}

            <StatusBar connected={connected} />
        </div>
    )
}

function SuccessView({
    orderCode,
    amount,
}: {
    orderCode: string
    amount: number
}) {
    return (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 animate-in fade-in duration-500">
            <CheckCircle className="h-24 w-24 text-green-500" />
            <h2 className="text-2xl font-bold text-green-600">
                {m.customer_display_success()}
            </h2>
            <p className="text-4xl font-bold text-gray-900">
                {formatVND(amount)}
            </p>
            <p className="text-sm text-gray-500">{orderCode}</p>
            <p className="text-lg text-gray-600 mt-4">
                {m.customer_display_thank_you()}
            </p>
        </div>
    )
}

function CartView({
    items,
    total,
    customerName,
    connected,
}: {
    items: CustomerDisplayCartItem[]
    total: number
    customerName: string
    connected: boolean
}) {
    return (
        <div className="flex-1 flex flex-col p-6">
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-700">
                    {m.customer_display_items()}
                </h2>
                {customerName && (
                    <span className="text-sm text-gray-500">
                        {customerName}
                    </span>
                )}
            </div>

            {/* Item list */}
            <div className="flex-1 overflow-y-auto py-3 space-y-2">
                {items.map((item, idx) => (
                    <div
                        key={`${item.name}-${idx}`}
                        className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                    >
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                                {item.name}
                            </p>
                            <p className="text-xs text-gray-500">
                                {item.qty} x {formatVND(item.price)}
                                {item.discount > 0 && (
                                    <span className="ml-1 text-orange-500">
                                        -{item.discount}%
                                    </span>
                                )}
                            </p>
                        </div>
                        <span className="font-semibold text-gray-900">
                            {formatVND(item.subtotal)}
                        </span>
                    </div>
                ))}
            </div>

            {/* Total */}
            <div className="pt-3 border-t-2 border-gray-300">
                <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-gray-700">
                        {m.customer_display_total()}
                    </span>
                    <span className="text-3xl font-bold text-primary">
                        {formatVND(total)}
                    </span>
                </div>
            </div>

            <StatusBar connected={connected} />
        </div>
    )
}

function CheckoutView({
    orderCode,
    total,
    paid,
    change,
}: {
    orderCode: string
    total: number
    paid: number
    change: number
}) {
    return (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 p-6 animate-in fade-in duration-500">
            <CheckCircle className="h-20 w-20 text-green-500" />

            <div className="w-full max-w-sm space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">
                        {m.customer_display_total()}
                    </span>
                    <span className="text-2xl font-bold text-gray-900">
                        {formatVND(total)}
                    </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">
                        {m.customer_display_customer_paid()}
                    </span>
                    <span className="text-xl font-medium text-gray-900">
                        {formatVND(paid)}
                    </span>
                </div>
                {change > 0 && (
                    <div className="flex items-center justify-between py-2 bg-green-50 rounded-lg px-3">
                        <span className="text-green-700 font-medium">
                            {m.customer_display_change()}
                        </span>
                        <span className="text-2xl font-bold text-green-600">
                            {formatVND(change)}
                        </span>
                    </div>
                )}
            </div>

            <p className="text-sm text-gray-400">{orderCode}</p>
            <p className="text-lg text-gray-600">
                {m.customer_display_thank_you()}
            </p>
        </div>
    )
}

function StatusBar({ connected }: { connected: boolean }) {
    const [time, setTime] = useState(new Date())
    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 30_000)
        return () => clearInterval(timer)
    }, [])

    return (
        <div className="absolute bottom-4 right-4 flex items-center gap-3 text-xs text-gray-400">
            <span
                className={`flex items-center gap-1 px-2 py-1 rounded-full ${
                    connected
                        ? 'bg-green-50 text-green-600'
                        : 'bg-red-50 text-red-500'
                }`}
            >
                {connected ? (
                    <Wifi className="h-3 w-3" />
                ) : (
                    <WifiOff className="h-3 w-3" />
                )}
                {connected
                    ? m.customer_display_connected()
                    : m.customer_display_disconnected()}
            </span>
            <span className="font-mono">
                {time.toLocaleTimeString('vi-VN', {
                    hour: '2-digit',
                    minute: '2-digit',
                })}
            </span>
        </div>
    )
}
